import {
  Box,
  Button,
  Grid,
  IconButton,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import { EditRounded } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import HighlightOffOutlined from "@material-ui/icons/HighlightOffOutlined";
import { postImages } from "adapters";
import { FileReader } from "components/shared/file-reader";
import { theme } from "material-theme";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import Slider, { Settings } from "react-slick";
import { SetUrlBuilderData, UploadType } from "store/url-builder";
import { DialogWrapper, DialogWrapperType } from "../dialog";
import { FileImportDialog } from "../file-import-dialog";
import style from "./file-carousel.module.scss";

const Carousel: any = withStyles({
  "@global": {
    ".slick-slider": {
      height: "100%",
    },
    ".slick-list": {
      height: "100%",
    },
    ".slick-track": {
      height: "100%",
    },
    ".slick-slide > div": {
      height: "100%",
    },
  },
})(Slider);

export interface FileCarouselProps {
  files: UploadType[];
  themeColor?: string;
  fileLimit?: number;
  readonly?: boolean;
  slidesPerRow?: number;
  enablePreview?: boolean;
  defaultSlideIndex?: number;
  builderType: string;
}

export const FileCarousel = ({
  files = [],
  themeColor,
  fileLimit = 10,
  readonly = false,
  slidesPerRow = 3,
  enablePreview = false,
  defaultSlideIndex = 0,
  builderType = "",
}: FileCarouselProps) => {
  const useStyles = useCallback(
    makeStyles(() => ({
      dots: {
        bottom: 0,
        "& li.slick-active button::before": {
          color: themeColor + "!important",
        },
      },
    })),
    [themeColor]
  );
  const [slides, setSlides] = useState(files);
  const [importDialog, setImportDialog] = useState<{
    slide?: UploadType;
    index?: number;
    open: boolean;
  } | null>(null);
  const [previewSlide, setPreviewSlide] = useState<{
    slide: UploadType;
    index: number;
  } | null>();
  const dispatch = useDispatch();
  const { cardId }: any = useParams();
  const sliderRef: any = useRef();
  const [replaceInProgres, setReplaceInProgreess] = useState(false);

  const classes = useStyles();
  const settings: Settings = {
    dots: true,
    infinite: false,
    slidesPerRow,
    dotsClass: `slick-dots ${classes.dots}`,
    centerPadding: "150px",
    initialSlide: 3,
    draggable: false,
    swipe: false,
  };

  useEffect(() => {
    setSlides(files);
  }, [files]);

  useEffect(() => {
    sliderRef?.current?.slickGoTo(defaultSlideIndex);
  }, [defaultSlideIndex]);

  const handleSlideRemove = async (index: number) => {
    const removeSlide = window.confirm(
      "Are you sure you want to remove selected slide?"
    );
    if (!removeSlide) return;

    let newSlides = slides;
    if (!replaceInProgres) {
      newSlides.splice(index, 1);
      const builderResponse = (await postImages(cardId, builderType, newSlides))
        .data;
      if (builderResponse.success)
        dispatch(SetUrlBuilderData(builderResponse.data));
    } else {
      const emptyUpload: UploadType = {
        _id: "",
        fileName: "",
        filePath: "",
        mimeType: "Empty placeholder",
        updatedAt: "",
      };
      newSlides[index] = emptyUpload;
      setImportDialog({ open: true, index: index });
    }
  };

  const handleFileUploaded = async (file: UploadType) => {
    let newSlides = slides;
    // edit existing slide
    if (importDialog?.index !== undefined) {
      newSlides[importDialog?.index] = file;
    }
    // add new slide
    else newSlides = [...newSlides, file];

    const builderResponse = (await postImages(cardId, builderType, newSlides))
      .data;
    setReplaceInProgreess(false);
    if (builderResponse.success) {
      dispatch(SetUrlBuilderData(builderResponse.data));
      setImportDialog(null);
    }
  };

  const openFilePreview = (slide: UploadType, index: number) => {
    if (enablePreview) return setPreviewSlide({ slide, index });

    if (!/image|video/.test(slide.mimeType)) window.open(slide.fileUrl);
  };

  return (
    <>
      <Carousel
        {...settings}
        ref={sliderRef}
        className={`${style.carousel} file-slide`}
        onInit={console.log}
      >
        {slides.map((slide, idx) => (
          <Box
            key={idx}
            boxSizing="border-box"
            padding=".3em"
            className="preview-content"
          >
            <Box
              display="flex"
              border={!readonly && "2px dashed #d5d5d5"}
              width="100%"
              height={!readonly ? "180px" : "100%"}
              position="relative"
              className="slide-box"
            >
              {!readonly && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    zIndex: 2,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setReplaceInProgreess(true);
                      setImportDialog({ slide, index: idx, open: true });
                    }}
                  >
                    <EditRounded
                      style={{ color: theme.palette.warning.main }}
                    />
                  </IconButton>
                  <IconButton onClick={() => handleSlideRemove(idx)}>
                    <HighlightOffOutlined
                      style={{ color: theme.palette.error.main }}
                    />
                  </IconButton>
                </Box>
              )}
              <Box
                sx={{ display: "flex", height: "100%", width: "100%" }}
                className="c-pointer"
                onClick={() => openFilePreview(slide, idx)}
              >
                <FileReader
                  file={slide}
                  videoConfig={{ controls: !enablePreview }}
                />
              </Box>
            </Box>
          </Box>
        ))}
        {!readonly && files.length < fileLimit && (
          <Box
            key={"add-new"}
            // height="100%"
            boxSizing="border-box"
            padding=".3em"
          >
            <Box
              display="flex"
              border={"2px dashed #d5d5d5"}
              width="100%"
              height="180px"
              position="relative"
              className="slide-box"
            >
              <Button
                className={style.addNewSlide}
                onClick={() => setImportDialog({ open: true })}
              >
                <AddIcon /> Add new Slide
              </Button>
            </Box>
          </Box>
        )}
      </Carousel>
      {importDialog && (
        <FileImportDialog
          onFileUploaded={handleFileUploaded}
          onFileRemoved={() => handleSlideRemove(importDialog.index as number)}
          open
          onClose={() => {
            setImportDialog(null);
            setReplaceInProgreess(false);
          }}
          preselectfile={importDialog.slide}
        />
      )}
      {enablePreview && previewSlide && (
        <DialogWrapper
          open
          title={`Gallery`}
          onClose={() => setPreviewSlide(null)}
          type={DialogWrapperType.DEFAULT}
          maxWidth="lg"
          height="80vh"
          enableFullScreen
          className="preview-box"
        >
          <Grid item xs={12} style={{ height: "100%" }}>
            <FileCarousel
              files={slides}
              slidesPerRow={1}
              readonly
              defaultSlideIndex={previewSlide.index}
              themeColor={themeColor}
              builderType={builderType}
            />
          </Grid>
        </DialogWrapper>
      )}
    </>
  );
};
